import {getTranslations, I18nStrings} from './i18n';
import {handleUIMode} from './ui/resolver';
import {registerToolbarButtons} from './ui/buttons';
import {registerCommands} from './core/commands';
import {ContentHandlers} from './core/contentHandlers';
import {NativeDialog} from './ui/native/dialog';
import {EmbeddedModal} from './ui/embedded/modal';
import {ExternalEvents} from './ui/external/events';
import {CardFace} from './types/flipcardTypes';
import {cardGlobalStyles} from "./styles/card-global-styles";
import {flipCardContainerClass, flipCardFaceClass, flipCardFrontFaceClass} from "./styles";
import {
  flipcardCardIdAttr,
  flipcardCardNumberAttr,
  flipcardCardParamAttr
} from "@/components/tinyMce/plugins/flipcard/flipcardConstants";
import {updateCardHeader, updateFooterButton} from "@/components/tinyMce/plugins/flipcard/core/content";

export default function FlipCardPlugin() {
  return (editor: any) => {
    // Configuration
    const locale = editor.getParam('flipcard_i18n_locale', 'en');
    const i18n: I18nStrings = getTranslations(locale);

    // Résolution du mode UI
    const uiMode = handleUIMode(editor);

    let customStylesRecord: Record<string, string> = {};
    let draggedCardId: string | null = null;

    // Initialiser les gestionnaires selon le mode
    let nativeDialog: NativeDialog | null = null;
    let embeddedModal: EmbeddedModal | null = null;
    let externalEvents: ExternalEvents | null = null;

    // Initialiser les gestionnaires UI
    if (uiMode === 'native') {
      nativeDialog = new NativeDialog(editor, i18n);
    } else if (uiMode === 'embedded') {
      embeddedModal = new EmbeddedModal(editor, i18n);
    } else if (uiMode === 'external') {
      externalEvents = new ExternalEvents(editor);
    }

    // Fonction pour gérer l'insertion d'image
    const handleInsertImage = (cardId: string, face: CardFace) => {
      const imageUploadUrl: string = editor.getParam('flipcard_image_upload_url', '/user/lib/photo');
      if (uiMode === 'native' && nativeDialog) {
        nativeDialog.handleImageInsertion(cardId, face, imageUploadUrl);
      } else if (uiMode === 'embedded' && embeddedModal) {
        embeddedModal.handleImageInsertion(cardId, face, imageUploadUrl);
      } else if (uiMode === 'external' && externalEvents) {
        externalEvents.handleImageInsertion(cardId, face);
      }
    };

    // Fonction pour gérer la suppression d'une card
    const handleCardDelete = (cardId: string) => {
      if (externalEvents) {
        externalEvents.handleCardDelete(cardId);
      }
    };

    // Initialiser ContentHandlers
    const contentHandlers = new ContentHandlers(editor, i18n, handleInsertImage, handleCardDelete);

    // Enregistrer les commandes
    registerCommands(editor, i18n, contentHandlers);

    // Enregistrer les boutons de la toolbar
    registerToolbarButtons(editor, i18n);

    // Setup: Attacher les événements aux cards existantes
    editor.on('init', () => {
      const style = editor.getDoc().createElement('style');

      // create custom styles given plugin params
      customStylesRecord = createCustomStylesRecord(editor);
      const customStyleSheet = Object.keys(customStylesRecord).length > 0 ? createCustomStyleSheet(customStylesRecord) : '';

      // Injecter le CSS
      style.textContent = cardGlobalStyles + customStyleSheet;
      editor.getDoc().head.appendChild(style);

      contentHandlers.rebindEventListenersToAllCards();
    });

    editor.on('dragstart', (e: any) => {
      const card: any = (e.target as HTMLElement).closest(flipCardContainerClass);
      if (card) {
        draggedCardId = card.getAttribute(flipcardCardIdAttr) || null;
        card.classList.add('dragging');
      }
    });

    editor.on('dragend', (e: any) => {
      if (draggedCardId) {
        const currentCard = editor.dom.select(`[${flipcardCardIdAttr}="${draggedCardId}"]`)[0] as HTMLElement;
        if (currentCard) {
          currentCard.classList.remove('dragging');
          contentHandlers.rebindEventListenersToAllCards();
          contentHandlers.updateCardNumbers();
        }
        draggedCardId = null;
        e.preventDefault();
      }
    });

    // Attacher les événements après un SetContent
    editor.on('SetContent', () => {
      setTimeout(() => {
        contentHandlers.rebindEventListenersToAllCards();
      }, 100);
    });

    editor.on('BeforeGetContent', () => {
      handleEditorOnBeforeGetContent();
    });

    editor.on('GetContent', (e: any) => {
      handleEditorOnGetContent(e);
    });

    // Nettoyer à la destruction
    editor.on('remove', () => {
      if (embeddedModal) {
        embeddedModal.destroy();
      }
    });

    function handleEditorOnBeforeGetContent() {
      const cards = editor.dom.select(flipCardContainerClass);
      cards.forEach((card: HTMLElement) => {
        // Sauvegarder les couleurs custom comme data attributes
        Object.entries(customStylesRecord).forEach(([cssVar, value]) => {
          card.setAttribute(`${flipcardCardParamAttr}-${cssVar.replace('--flipcard-', '')}`, value);
        });
        // On supprimer les attributs data-events-attached avant sauvegarde
        delete card.dataset.eventsAttached;
      });
    }

    function handleEditorOnGetContent(e: any) {
      // Flip toutes les cartes en mode front avant sauvegarde
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = e.content;

      const flipcards = tempDiv.querySelectorAll(flipCardContainerClass);
      flipcards.forEach(card => {
        // Forcer toutes les faces en mode front
        const faces = card.querySelectorAll(flipCardFaceClass);
        faces.forEach((face: Element) => face.classList.remove('active'));
        const frontFace = card.querySelector(flipCardFrontFaceClass);
        if (frontFace) {
          frontFace.classList.add('active');
        }
        const cardNumber = parseInt(card.getAttribute(flipcardCardNumberAttr) || '1', 10);
        updateCardHeader(card as HTMLElement, cardNumber, 'front', i18n);
        updateFooterButton(card as HTMLElement, 'front', i18n);
      });
      // Retourner le HTML modifié
      e.content = tempDiv.innerHTML;
    }

    // Retourner les informations du plugin
    return {
      getMetadata: () => ({
        name: 'FlipCard Plugin',
        author: 'Speach',
        description: 'Interactive flip cards with hybrid UI architecture',
        uiMode,
        locale
      })
    };
  };
}

function createCustomStylesRecord(editor: any) {
  const customVars: Record<string, string> = {};

  if (editor.getParam('flipcard_header_bg')) {
    customVars['--flipcard-header-bg'] = editor.getParam('flipcard_header_bg');
  }
  if (editor.getParam('flipcard_footer_bg')) {
    customVars['--flipcard-footer-bg'] = editor.getParam('flipcard_footer_bg');
  }
  if (editor.getParam('flipcard_button_color')) {
    customVars['--flipcard-button-color'] = editor.getParam('flipcard_button_color');
  }
  if (editor.getParam('flipcard_button_hover_color')) {
    customVars['--flipcard-button-hover-color'] = editor.getParam('flipcard_button_hover_color');
  }
  if (editor.getParam('flipcard_text_color')) {
    customVars['--flipcard-text-color'] = editor.getParam('flipcard_text_color');
  }
  if (editor.getParam('flipcard_readmode_flipbtn_color')) {
    customVars['--flipcard-readmode-flipbtn-color'] = editor.getParam('flipcard_readmode_flipbtn_color');
  }
  if (editor.getParam('flipcard_readmode_flipbtn_hover_color')) {
    customVars['--flipcard-readmode-flipbtn-hover-color'] = editor.getParam('flipcard_readmode_flipbtn_hover_color');
  }
  if (editor.getParam('flipcard_border_color')) {
    customVars['--flipcard-border-color'] = editor.getParam('flipcard_border_color');
  }
  if (editor.getParam('flipcard_drag_handle_color')) {
    customVars['--flipcard-drag-handle-color'] = editor.getParam('flipcard_drag_handle_color');
  }
  if (editor.getParam('flipcard_icon_color')) {
    customVars['--flipcard-icon-color'] = editor.getParam('flipcard_icon_color');
  }
  if (editor.getParam('flipcard_option_icon_color')) {
    customVars['--flipcard-option-icon-color'] = editor.getParam('flipcard_option_icon_color');
  }
  if (editor.getParam('flipcard_separator_color')) {
    customVars['--flipcard-separator-color'] = editor.getParam('flipcard_separator_color');
  }
  if (editor.getParam('flipcard_icon_hover_color')) {
    customVars['--flipcard-icon-hover-color'] = editor.getParam('flipcard_icon_hover_color');
  }
  if (editor.getParam('flipcard_option_hover_bg')) {
    customVars['--flipcard-option-hover-bg'] = editor.getParam('flipcard_option_hover_bg');
  }
  return customVars;
}

function createCustomStyleSheet(vars: Record<string, string>): string {
  const rules = Object.entries(vars)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n  ');

  return `:root {\n  ${rules}\n}`;
}
